<template>
  <div>
    <div class="flex items-center justify-between flex-wrap gap-sm mb-xl md:mb-2xl">
      <h2>
        {{ 'Return Order' | t }} #{{ returnRequest?.rma | formatOrderNumber }}
      </h2>
      <SloLink
        v-if="authenticated"
        :to="$route.query.back || {name: 'marketplace-account-returns'}"
      >
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloLink>
      <SloLink v-else :to="{name: 'marketplace-auth-login'}">
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloLink>
    </div>
    <MarketplaceTable>
      <template v-slot:header>
        <MarketplaceTableHeader>
          <template v-slot:titles>
            <div class="flex items-start justify-between md:flex-col md:justify-start">
              <h4 class="text-base fw-strong">{{ 'Return Order Number' | t }}</h4>
              <span class="text-base-c-gray-700">{{ returnRequest?.rma | formatOrderNumber }}</span>
            </div>
            <div class="flex items-start justify-between md:flex-col md:justify-start">
              <h4 class="text-base fw-strong">{{ 'Date Placed' | t }}</h4>
              <span class="text-base-c-gray-700">{{ returnRequest?.creationDate }}</span>
            </div>
            <div class="flex items-start justify-between md:flex-col md:justify-start">
              <h4 class="text-base fw-strong">{{ 'Items' | t }}</h4>
              <span class="text-base-c-gray-700">{{ returnRequest?.totalItems }}</span>
            </div>
          </template>
          <template v-slot:buttons>
            <SloButton
              fill="outline"
              @click="handlePrintOrder"
            >
              {{ 'Print Order' | t }}
            </SloButton>
            <SloButton
              v-if="returnRequest?.docType"
              @click="handleInvoiceDownload(returnRequest?.docType)"
              fill="outline"
            >
              {{ 'View Invoice' | t }}
            </SloButton>
          </template>
        </MarketplaceTableHeader>
      </template>
      <template v-slot:body>
        <MarketplaceRow
          v-for="(returnEntry, index) in returnRequest?.returnEntries"
          :key="`return-entry-${index}`"
          :title="returnEntry?.orderEntry?.product?.number"
          :description="returnEntry?.orderEntry?.product?.title"
          :sku="returnEntry?.orderEntry?.product?.code"
          :product-type="returnEntry?.orderEntry?.product?.type"
          :price="returnEntry?.orderEntry?.basePrice?.formattedValue"
          :uom="returnEntry?.orderEntry?.product?.uom"
          :quantity="returnEntry?.expectedQuantity"
          :total="returnEntry?.refundAmount?.formattedValue"
          :show-reorder-button="true"
        >
          <template v-slot:image>
            <SloProductImage :source="returnEntry?.orderEntry?.product?.images | getProductThumbnail" />
          </template>
        </MarketplaceRow>
      </template>
      <template v-slot:customer-return>
        <div class="customer-return">
          <div class="flex flex-col items-start gap-sm">
            <p v-if="returnRequest?.refundReason" class="flex items-start gap-sm mb-0">
              <span class="text-base fw-strong">{{ 'Reason' | t }}</span>
              {{ returnRequest?.refundReason }}
            </p>
            <p v-if="returnRequest?.notes" class="flex items-start gap-sm mb-0">
              <span class="text-base fw-strong">{{ 'Notes' | t }}</span>
              {{ returnRequest?.notes }}
            </p>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <MarketplaceTableFooter>
          <div class="flex flex-col items-start justify-between gap-md w-100 md:flex-row">
            <p class="flex-1 w-100 text-base fw-strong m-0">{{ 'Purchased from Sloan Marketplace' | t }}</p>
            <div class="flex-1 w-100">
              <div class="marketplace--returns-total-refund-amount">
                <div class="flex items-start justify-between gap-sm w-100">
                  <p class="m-0">{{ 'Subtotal' | t }}</p>
                  <p class="m-0">{{ returnRequest?.subTotal?.formattedValue }}</p>
                </div>
                <div class="flex items-start justify-between gap-sm w-100">
                  <p class="m-0">{{ 'Shipping' | t }}</p>
                  <p class="m-0">{{ returnRequest?.shippingCost?.formattedValue }}</p>
                </div>
                <div class="flex items-start justify-between gap-sm w-100">
                  <p class="m-0">{{ 'Tax' | t }}</p>
                  <p class="m-0">{{ returnRequest?.tax?.formattedValue }}</p>
                </div>
              </div>
              <div class="flex items-start justify-between gap-sm">
                <p class="text-lg fw-strong m-0">{{ 'Total refund amount' | t }}</p>
                <p class="m-0">{{ returnRequest?.totalPrice?.formattedValue }}</p>
              </div>
            </div>
          </div>
        </MarketplaceTableFooter>
      </template>
    </MarketplaceTable>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import t from '../../main/i18n';
import {getProductThumbnail, formatOrderNumber} from '../../main/utils';
import MarketplaceRow from './MarketplaceRow.vue';
import MarketplaceTable from './MarketplaceTable.vue';
import MarketplaceTableFooter from './MarketplaceTableFooter.vue';
import MarketplaceTableHeader from './MarketplaceTableHeader.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';

export default {
  name: 'ReturnDetails',
  components: {
    MarketplaceTable,
    MarketplaceTableHeader,
    MarketplaceRow,
    MarketplaceTableFooter,
    SloProductImage,
    SloButton,
    SloLink,
    SloIcon
  },
  filters: {
    t,
    getProductThumbnail,
    formatOrderNumber
  },
  props: {
    returnRequest: {type: Object, required: true}
  },
  computed: mapGetters('auth', ['access_token', 'authenticated']),
  methods: {
    handlePrintOrder() {
      window.print();
    },
    handleInvoiceDownload(docType) {
      fetch(
        `${window.location.origin}/marketplace/api` +
        `/users/${this.$store.getters['marketplace/userId']}` +
        `/getPdf?documentNumber=${docType}`,
        this.access_token ? {headers: {Authorization: `Bearer ${this.access_token}`}} : {}
      )
        .then(resp => resp.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `${docType}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => this.$snotify.error(t('Error downloading invoice')));
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace--returns-total-refund-amount {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 0.8rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1.6rem;
  border-bottom: 1px solid $color-gray-400;
}

h2 {
  font-weight: normal;
  font-size: 3.6rem;
  margin: 0;

  @media (min-width: $screen-sm) {
    @include text('3xl');
  }
}

.customer-return {
  padding: 1.6rem;
  border-top: 1px solid $color-gray-400;
  background-color: #fffbeb;
}
</style>
